/**
 * Motor TT (Trinidad & Tobago) helper functions.
 *
 * Extracts all Motor-TT-specific logic out of the shared wizard reducer
 * so the root reducer file stays product-agnostic.
 *
 * NOTE: This file lives in src/reducers/wizards/ (not ggilComponents) because
 * these functions are reducer-level logic — they derive state and transform
 * API responses. The reducer imports them directly, and having components
 * imported by reducers would invert the data flow and risk circular deps.
 *
 * Exports:
 *  - applyDerivedMotorState(wizardData) — derives vehicleAge
 *  - transformPremiumResponse(newState, action) — transforms API
 *    premium response into the array consumed by AccordionChoice
 *  - buildSelectedPremiumFromCoverType(wizardData, stateWizardData) — builds
 *    the breakdown array for DisplayListItems when a plan is selected
 */

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Safely extract a numeric value from wizard data.
 *  Wizard data can store values as strings, numbers, or {value, title} objects. */
const toNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') {
    return Number.isNaN(value) ? null : value;
  }
  if (typeof value === 'object') {
    if (Object.prototype.hasOwnProperty.call(value, 'value')) {
      return toNumber(value.value);
    }
    if (Object.prototype.hasOwnProperty.call(value, 'title')) {
      return toNumber(value.title);
    }
  }
  const normalized = value
    .toString()
    .replace(/,/g, '')
    .replace(/[^0-9.-]/g, '');
  if (!normalized) return null;
  const num = Number(normalized);
  return Number.isNaN(num) ? null : num;
};

// ─── Vehicle Age Derivation ──────────────────────────────────────────────────

/**
 * Derive vehicle age from manufacture year.
 * Stored as `vehicleAge` in allInitialWizardData. The JSON references this
 * value in 14+ conditions (e.g., vehicleAge >= 20, coverage + age rules).
 * This computation MUST stay in JS because JSON cannot do arithmetic
 * (currentYear - manufactureYear). All other Motor TT conditions (referredTo,
 * underwriterLoading) are fully handled by param-level conditions in
 * basicMotorInsurance.json.
 */
const deriveVehicleAgeFromWizardData = (wizardData) => {
  if (!wizardData) return null;
  const vehicleManufactureYear = toNumber(
    wizardData?.vehicleManufactureYear ||
      wizardData?.vehicleYear ||
      wizardData?.yearOfManufacture ||
      wizardData?.vehicleManufactureYear?.value,
  );
  if (vehicleManufactureYear === null || vehicleManufactureYear === undefined)
    return null;

  const currentYear = new Date().getFullYear();
  if (vehicleManufactureYear > currentYear) return null;
  return currentYear - vehicleManufactureYear;
};

/**
 * Derives vehicleAge from wizard data.
 * Called in every reducer case that modifies allInitialWizardData.
 *
 * NOTE: All other Motor TT derivations have been moved to JSON:
 * - referredTo: ~20 OR'd param-level conditions on each premium API call
 * - underwriterLoadingCode/Percent: inline condition CubicCapacityRating >= 3500
 *   with staticValue "UND"/"25" on each premium API param
 * - Address copying: CheckChoice_appendKeys in basicMotorInsurance.json
 * vehicleAge is the only value that must be computed here because JSON
 * cannot perform arithmetic (currentYear - vehicleManufactureYear).
 */
// GgilCheckChoice name of the EV Battery benefit (note the trailing space —
// it matches the wizard JSON exactly and is the key the checkbox reads/writes).
const EV_BATTERY_CHECK_KEY = 'Electric Vehicle Battery Replacement or Repair ';

export const applyDerivedMotorState = (wizardData) => {
  if (!wizardData) return wizardData;
  let nextData = wizardData;

  const vehicleAge = deriveVehicleAgeFromWizardData(wizardData);
  if (vehicleAge !== null && vehicleAge !== wizardData.vehicleAge) {
    nextData = { ...nextData, vehicleAge };
  }
  if (vehicleAge === null && wizardData.vehicleAge !== undefined) {
    nextData = { ...nextData };
    delete nextData.vehicleAge;
  }

  // Electric Vehicle Battery benefit is only offered on the Electric Guard (EVP)
  // plan for electric and hybrid vehicles. Its checkbox is hidden while
  // ineligible, and a hidden GgilCheckChoice skips its own sync
  // (`if (!show) return;`), so it can never clear itself. Without this, switching
  // plan away from EVP (or changing fuel type) leaves the selection set, and it
  // reappears pre-checked when the user returns to EVP. Clear it here whenever
  // the benefit is ineligible.
  const evBatteryFuelType = (wizardData.vehicleFuelType || '')
    .toString()
    .trim()
    .toLowerCase();
  const isEvBatteryEligible =
    (evBatteryFuelType === 'electric' || evBatteryFuelType === 'hybrid') &&
    (wizardData.coverType || '').toString().trim().toUpperCase() === 'EVP';
  const hasEvBatterySelection =
    (Array.isArray(wizardData[EV_BATTERY_CHECK_KEY]) &&
      wizardData[EV_BATTERY_CHECK_KEY].length > 0) ||
    !!wizardData[`${EV_BATTERY_CHECK_KEY}Payload`] ||
    !!wizardData.electricVehicleBatteryOption ||
    !!wizardData.ElectricVehicleBattery;
  if (!isEvBatteryEligible && hasEvBatterySelection) {
    nextData = { ...nextData };
    nextData[EV_BATTERY_CHECK_KEY] = [];
    nextData[`${EV_BATTERY_CHECK_KEY}Payload`] = null;
    nextData.electricVehicleBatteryOption = '';
    nextData.ElectricVehicleBattery = null;
  }

  return nextData;
};

// ─── Premium Response → UI Transformation ────────────────────────────────────

/** Map plan codes to friendly display names */
const planTitleMap = {
  QGD: 'Queenguard',
  PGD: 'Primeguard',
  STD: 'Standard',
  HVV: 'High Value Vehicles',
  GMP: 'Guardian Master Plan',
  CLP: 'Loan to Road Campaign',
  EVP: 'Electric Guard',
};

/** Build the coverageMatrix features array based on user selections */
const buildCoverageFeatures = ({
  isThirdPartySelection,
  isThirdPartyOnlySelection,
  selectedCoverTypeLabel,
  isPickupBodyType,
}) => [
  {
    key: 'coverType',
    title: 'Cover Type',
    values: {
      QGD: 'Comprehensive',
      HVV: 'Comprehensive',
      GMP: 'Comprehensive',
      CLP: 'Comprehensive',
      EVP: 'Comprehensive',
      PGD: isThirdPartySelection ? selectedCoverTypeLabel : 'Comprehensive',
      STD: isThirdPartySelection ? selectedCoverTypeLabel : 'Comprehensive',
    },
  },
  {
    key: 'tpbpd',
    title: 'Third-Party Liability Coverage (Bodily Injury & Property Damage)',
    values: {
      QGD: true,
      HVV: true,
      GMP: true,
      CLP: true,
      EVP: true,
      PGD: true,
      STD: true,
    },
  },
  {
    key: 'clothing',
    title: 'Clothing & Personal Effects',
    values: {
      QGD: true,
      HVV: true,
      GMP: true,
      CLP: true,
      EVP: true,
      PGD: isThirdPartySelection ? null : true,
      STD: isThirdPartySelection ? null : true,
    },
  },
  {
    key: 'legal',
    title: 'Legal Charges (Manslaughter)',
    values: {
      QGD: true,
      HVV: true,
      GMP: true,
      CLP: true,
      EVP: true,
      PGD: isThirdPartyOnlySelection || (isThirdPartySelection ? null : true),
      STD: true,
    },
  },
  {
    key: 'emergency',
    title: 'Emergency Treatment',
    values: {
      QGD: true,
      HVV: true,
      GMP: true,
      CLP: true,
      EVP: true,
      PGD: isThirdPartyOnlySelection || (isThirdPartySelection ? null : true),
      STD: true,
    },
  },
  {
    key: 'childseat',
    title: 'Child Car Seat Benefit',
    values: {
      QGD: true,
      HVV: true,
      GMP: true,
      CLP: true,
      EVP: true,
      PGD: isThirdPartySelection ? null : true,
      STD: isThirdPartySelection ? null : true,
    },
  },
  {
    key: 'replacementkeys',
    title: 'Replacement Keys',
    values: {
      QGD: true,
      HVV: true,
      GMP: true,
      CLP: true,
      EVP: true,
      PGD: isThirdPartySelection ? null : true,
      STD: isThirdPartySelection ? null : true,
    },
  },
  {
    key: 'medical',
    title: 'Medical Expenses',
    values: {
      QGD: true,
      HVV: true,
      GMP: true,
      CLP: true,
      EVP: true,
      PGD: isThirdPartySelection ? null : true,
      STD: isThirdPartySelection ? null : true,
    },
  },
  {
    key: 'personalacc',
    title: 'Personal Accident Benefit',
    values: {
      QGD: true,
      HVV: true,
      GMP: true,
      CLP: true,
      EVP: true,
      PGD: isThirdPartySelection ? null : true,
      STD: null,
    },
  },
  {
    key: 'specialperils',
    title: 'Special Perils',
    values: {
      QGD: true,
      HVV: true,
      GMP: true,
      CLP: true,
      EVP: true,
      PGD: isThirdPartySelection ? null : true,
      STD: isThirdPartySelection ? null : true,
    },
  },
  {
    key: 'antitheft',
    title: 'Anti Theft Discount',
    values: {
      QGD: true,
      HVV: true,
      GMP: true,
      CLP: true,
      EVP: true,
      PGD: isThirdPartySelection ? null : true,
      STD: isThirdPartySelection ? null : true,
    },
  },
  {
    key: 'specialdiscount',
    title: 'Special Discount',
    values: {
      QGD: true,
      HVV: true,
      GMP: true,
      CLP: true,
      EVP: true,
      PGD: isThirdPartyOnlySelection || (isThirdPartySelection ? null : true),
      STD: true,
    },
  },
  {
    key: 'windscreen',
    title: 'Windscreen Cover (All Glass)',
    values: {
      QGD: 5000,
      HVV: 7500,
      GMP: 2500,
      CLP: 5000,
      EVP: 2500,
      PGD: isThirdPartySelection ? null : 5000,
      STD: isThirdPartySelection ? null : 2500,
    },
  },
  {
    key: 'courtesycar',
    title: 'Courtesy Car/Loss Of Use',
    values: {
      QGD: isPickupBodyType ? null : true,
      HVV: isPickupBodyType ? null : true,
      GMP: isPickupBodyType ? null : true,
      CLP: isPickupBodyType ? null : true,
      EVP: isPickupBodyType ? null : true,
      PGD: isPickupBodyType ? null : isThirdPartySelection ? null : true,
      STD: isPickupBodyType ? null : isThirdPartySelection ? null : true,
    },
  },
  { key: 'agreeddep', title: 'Agreed Depreciation', values: { QGD: true } },
  {
    key: 'motorassist',
    title: 'Motor Assist (Road Side Assistance)',
    values: {
      QGD: true,
      HVV: true,
      GMP: true,
      CLP: true,
      EVP: true,
      PGD: isThirdPartySelection ? null : true,
      STD: isThirdPartySelection ? null : true,
    },
  },
  {
    key: 'survivorbenefit',
    title: 'Survivor Benefit',
    values: { PGD: isThirdPartySelection ? null : true },
  },
];

const formatCurrencyValue = (v, currency) => {
  if (v === null || v === undefined) return null;
  try {
    const n = Number(v);
    const formatted = Number.isFinite(n) ? n.toLocaleString(undefined) : String(v);
    return `${currency} ${formatted}`;
  } catch (e) {
    return `${currency} ${v}`;
  }
};

/** Build whatsCovered shaped like the UI expects for a given plan key */
const buildWhatsCoveredForPlan = (
  features,
  planKey,
  currency,
  isThirdPartyOnlySelection,
) => {
  const items = features
    .filter(
      (f) =>
        !(
          isThirdPartyOnlySelection &&
          (f.key === 'clothing' ||
            f.key === 'childseat' ||
            f.key === 'antitheft' ||
            f.key === 'agreeddep')
        ),
    )
    .map((f) => {
      const hasKey = f.values && Object.prototype.hasOwnProperty.call(f.values, planKey);
      const v = hasKey ? f.values[planKey] : null;

      if (v === true) return { title: f.title, startIcon: 'check' };
      if (typeof v === 'number')
        return {
          title: f.title,
          secondaryTitle: formatCurrencyValue(v, currency),
          startIcon: null,
        };
      if (typeof v === 'string')
        return { title: f.title, secondaryTitle: v, startIcon: null };
      return { title: f.title, secondaryTitle: '-', startIcon: null };
    });

  return { items, primaryHeading: "What's Covered" };
};

/**
 * Find a raw plan object from the motorTT groups by plan key.
 * Used to store selectedPremiumRaw for other components.
 */
const findRawMotorTTPlan = (motorTT, planKey) => {
  let foundRaw = null;
  if (Array.isArray(motorTT)) {
    motorTT.forEach((group) => {
      Object.keys(group || {}).forEach((key) => {
        const plan = group[key];
        if (plan && key === planKey) {
          foundRaw = plan;
        }
      });
    });
  }
  return foundRaw;
};

/**
 * Wizard fields that change the motorTT premium / available plans. Used to
 * build a signature so we can tell, on a Coverage Plan step re-entry, whether
 * the user changed any rating input (→ refresh the plans) or merely navigated
 * back and forth (→ preserve the already-validated premium).
 *
 * Each entry is a list of aliases; the first non-empty value is used so the
 * signature is stable regardless of which alias the wizard populated.
 */
const PREMIUM_INPUT_FIELD_ALIASES = [
  ['vehicleCoverageType'],
  ['vehicleFuelType'],
  ['vehicleBodyType'],
  ['vehicleType'],
  ['vehicleUsage'],
  ['vehicleManufactureYear', 'vehicleYear', 'yearOfManufacture'],
  [
    'vehicleValue',
    'number_vehicleValue',
    'formatted_vehicleValue',
    'sumInsured',
    'marketValue',
  ],
  ['vehicleCC', 'ccRating', 'CubicCapacityRating'],
  // The motor wizard stores the seat count under `NumberofSeats` (the
  // GgilDropdownChoice name); `noOfSeats` is only the add-to-cart payload key
  // and is never present in wizardData. Listing `noOfSeats` first made the
  // signature blind to seat-count changes, so changing No. of Seats on the
  // Vehicle step never refreshed the validated premium — the benefit
  // checkboxes stayed applied while selectedPremiumRaw (the add-to-cart
  // `amount`) fell back to the base price, under-charging the receipt.
  ['NumberofSeats', 'noOfSeats'],
  ['dob', 'dobAge'],
  ['youngDrivers'],
  ['vehicleClaimsHistory'],
  ['noClaimPeriodDuration'],
  ['vehicleModified'],
  ['vehicleParkedArea'],
  ['existingMotorInsurance'],
];

/**
 * Build a stable string signature of the premium-affecting wizard inputs.
 * Two builds with identical rating inputs produce identical signatures.
 *
 * @param {object} wizardData — allInitialWizardData
 * @returns {string}
 */
export const buildPremiumInputSignature = (wizardData) => {
  const data = wizardData || {};
  return PREMIUM_INPUT_FIELD_ALIASES.map((aliases) => {
    const key = aliases.find(
      (k) => data[k] !== undefined && data[k] !== null && data[k] !== '',
    );
    const value = key ? data[key] : '';
    return `${aliases[0]}=${value === null || value === undefined ? '' : String(value).trim().toLowerCase()}`;
  }).join('|');
};

/**
 * Build a UI-friendly array from the premium API response.
 * Transforms the motorTT[] groups into the format that AccordionChoice
 * (viewType=comparisonTable) expects. Each item has title, price, sampleData
 * with breakdown and whatsCovered sections.
 *
 * Also handles:
 * - PrimeGuard eligibility filtering
 * - preserveCoverageSelection logic
 * - selectedPremiumListItems for already-selected plans
 *
 * Mutates newState.allInitialWizardData in place (same pattern as original).
 *
 * @param {object} newState — the reducer state being built (mutated in place)
 * @param {object} action — the redux action (for preserveCoverageSelection)
 */
export const transformPremiumResponse = (newState, action) => {
  try {
    const premiumResp = newState?.allInitialWizardData?.premiumResponse;
    const premiumRespData = premiumResp?.payload ?? premiumResp;
    const normalizedResp =
      Array.isArray(premiumRespData) && premiumRespData.length > 0
        ? premiumRespData[0]
        : premiumRespData || {};
    if (!normalizedResp) return;

    const motorTT = Array.isArray(normalizedResp.motorTT) ? normalizedResp.motorTT : [];
    // If the response contains no motorTT data this is not a motorTT premium
    // response. Return early so we don't clobber the `insurance` field (and
    // other motorTT-specific fields) for every other Dutch product.
    if (motorTT.length === 0) return;

    // Coverage Plan step re-entry: the auto-refresh API call omits the
    // optional-benefit selections, so its base-price response would overwrite
    // the with-benefits premium the user already validated. Preserve the
    // existing premiumResponseForUI + selectedPremiumListItems until the user
    // clicks "Click To Validate" again (which sends the benefit params).
    const existingBreakdown = newState?.allInitialWizardData?.selectedPremiumListItems;
    const hasExistingBreakdown =
      Array.isArray(existingBreakdown) && existingBreakdown.length > 0;
    // ...unless the user changed a premium-affecting input (coverage type, fuel
    // type, vehicle value, CC, year, body type, etc.) since the displayed plans
    // were last built. In that case the shown plans are stale and MUST refresh,
    // so the preserve guard is skipped and the old selection is dropped so the
    // user re-validates against the new inputs.
    const currentInputSignature = buildPremiumInputSignature(
      newState?.allInitialWizardData,
    );
    const lastBuiltInputSignature =
      newState?.allInitialWizardData?.lastBuiltPremiumSignature || '';
    const premiumInputsChanged =
      lastBuiltInputSignature !== '' && currentInputSignature !== lastBuiltInputSignature;
    if (
      hasExistingBreakdown &&
      !apiCallIncludesBenefitParams(action) &&
      !premiumInputsChanged
    )
      return;
    if (premiumInputsChanged) {
      newState.allInitialWizardData.selectedPremiumListItems = [];
      newState.allInitialWizardData.coverType = '';
      newState.allInitialWizardData.thirdPartyLiability = null;
      newState.allInitialWizardData.selectedPremiumRaw = null;
    }
    const vehicleManufactureYear = toNumber(
      newState?.allInitialWizardData?.vehicleManufactureYear ||
        newState?.allInitialWizardData?.vehicleYear ||
        newState?.allInitialWizardData?.yearOfManufacture ||
        newState?.allInitialWizardData?.vehicleManufactureYear?.value,
    );
    const vehicleValue = toNumber(
      newState?.allInitialWizardData?.vehicleValue ||
        newState?.allInitialWizardData?.number_vehicleValue ||
        newState?.allInitialWizardData?.formatted_vehicleValue ||
        newState?.allInitialWizardData?.sumInsured ||
        newState?.allInitialWizardData?.marketValue,
    );
    const currentYear = new Date().getFullYear();
    const vehicleAge =
      vehicleManufactureYear !== null ? currentYear - vehicleManufactureYear : null;
    const dobAge = toNumber(newState?.allInitialWizardData?.dobAge);
    const isPrimeGuardAgeEligible = dobAge !== null && dobAge <= 65;
    const isPrimeGuardLegacyAgeEligible = dobAge !== null && dobAge >= 50 && dobAge <= 65;

    // Selected cover type from wizard dropdown (C/T/F)
    const selectedCoverageType = newState?.allInitialWizardData?.vehicleCoverageType;
    const coverTypeLabelFromSelection = (coverageType) => {
      const t = (coverageType || '').toString().trim().toUpperCase();
      if (t === 'T') return 'Third Party';
      if (t === 'F') return 'Third Party, Fire & Theft';
      return 'Comprehensive';
    };
    const selectedCoverTypeLabel = coverTypeLabelFromSelection(selectedCoverageType);
    const tSel = (selectedCoverageType || '').toString().trim().toUpperCase();
    const isThirdPartySelection = tSel === 'T' || tSel === 'F';
    const isThirdPartyOnlySelection = tSel === 'T';
    const isPickupBodyType =
      (newState?.allInitialWizardData?.vehicleBodyType || '')
        .toString()
        .trim()
        .toLowerCase() === 'pickup';
    const fuelType = (newState?.allInitialWizardData?.vehicleFuelType || '')
      .toString()
      .trim()
      .toLowerCase();
    const isPrimeGuardFuelEligible = [
      'electric',
      'hybrid',
      'diesel',
      'petrol',
      'cng',
    ].includes(fuelType);
    const isPrimeGuardVehicleAgeEligible = vehicleAge !== null && vehicleAge <= 20;
    const isPrimeGuardLegacyEligible =
      vehicleAge !== null &&
      vehicleAge <= 10 &&
      vehicleValue !== null &&
      vehicleValue >= 100000 &&
      isPrimeGuardLegacyAgeEligible;
    const isPrimeGuardEligible =
      isPrimeGuardLegacyEligible ||
      (isThirdPartyOnlySelection &&
        isPrimeGuardVehicleAgeEligible &&
        isPrimeGuardAgeEligible &&
        isPrimeGuardFuelEligible);
    const isQueenGuardEligible = vehicleAge !== null && vehicleAge <= 2;

    const features = buildCoverageFeatures({
      isThirdPartySelection,
      isThirdPartyOnlySelection,
      selectedCoverTypeLabel,
      isPickupBodyType,
    });

    const uiArr = [];
    const formatNumber = (v) => (v === null || v === undefined ? null : Number(v));

    motorTT.forEach((group) => {
      if (!group || typeof group !== 'object') return;
      Object.keys(group).forEach((planKey) => {
        const plan = group[planKey];
        if (!plan || typeof plan !== 'object') return;
        if (planKey === 'PGD' && !isPrimeGuardEligible) return;
        if (planKey === 'QGD' && !isQueenGuardEligible) return;

        const price = plan.priceXcg ?? null;

        let premium = null;
        let tax = null;

        const scanArray = (arr) => {
          if (!Array.isArray(arr)) return;
          arr.forEach((entry) => {
            const t = (entry?.type || '').toLowerCase();
            const p = entry?.pricing?.premiumXcg ?? entry?.priceXcg ?? null;
            if (t.includes('insurance') || t.includes('premium')) {
              if (premium == null) premium = p;
            } else if (t.includes('tax')) {
              if (tax == null) tax = p;
            }
          });
        };

        scanArray(plan.result);
        scanArray(plan.premium);

        if (premium == null && plan.premiumTotal) premium = plan.premiumTotal;

        const currency = normalizedResp?.currency || 'TTD';

        const sampleData = {
          breakdown: {
            items: [
              {
                title: 'Premium',
                secondaryTitle: formatNumber(premium),
                secondaryUnits: currency,
                secondaryTitleIsCurrency: true,
                secondaryTitleIsNumeric: true,
              },
              {
                title: 'Government Insurance Tax',
                secondaryTitle: formatNumber(tax),
                secondaryUnits: currency,
                secondaryTitleIsCurrency: true,
                secondaryTitleIsNumeric: true,
              },
            ],
            primaryHeading: 'Breakdown',
            secondaryHeading: currency,
          },
          whatsCovered: buildWhatsCoveredForPlan(
            features,
            planKey,
            currency,
            isThirdPartyOnlySelection,
          ),
        };

        uiArr.push({
          title: planTitleMap[planKey] || plan.title || planKey,
          secondaryTitle: formatNumber(price != null ? price : null),
          secondaryTitleIsCurrency: true,
          secondaryTitleIsNumeric: true,
          secondaryUnits: currency,
          value: planKey,
          sampleData,
          hidden: false,
        });
      });
    });

    newState.allInitialWizardData.premiumResponseForUI = uiArr;
    newState.allInitialWizardData.insurance = 'motorTT';
    // Remember which premium-affecting inputs these displayed plans correspond
    // to, so a later auto-refresh can detect an input change and refresh instead
    // of preserving stale plans (see the premiumInputsChanged guard above).
    newState.allInitialWizardData.lastBuiltPremiumSignature = buildPremiumInputSignature(
      newState.allInitialWizardData,
    );

    // Clear PGD selection if no longer eligible
    if (!isPrimeGuardEligible && newState?.allInitialWizardData?.coverType === 'PGD') {
      newState.allInitialWizardData.coverType = '';
      newState.allInitialWizardData.selectedPremiumListItems = [];
      newState.allInitialWizardData.thirdPartyLiability = null;
      newState.allInitialWizardData.selectedPremiumRaw = null;
    }

    // Clear QGD selection if no longer eligible
    if (!isQueenGuardEligible && newState?.allInitialWizardData?.coverType === 'QGD') {
      newState.allInitialWizardData.coverType = '';
      newState.allInitialWizardData.selectedPremiumListItems = [];
      newState.allInitialWizardData.thirdPartyLiability = null;
      newState.allInitialWizardData.selectedPremiumRaw = null;
    }

    const preserveCoverageSelection =
      action?.apiCall?.preserveCoverageSelection === true ||
      (action?.apiCall?.preserveCoverageSelection !== false &&
        !!newState?.allInitialWizardData?.coverType);
    if (!preserveCoverageSelection) {
      newState.allInitialWizardData.selectedPremiumListItems = [];
      newState.allInitialWizardData.thirdPartyLiability = null;
      newState.allInitialWizardData.coverType = '';
      newState.allInitialWizardData.selectedPremiumRaw = null;
    } else {
      applySelectedCoverType(newState.allInitialWizardData, uiArr, motorTT);
    }
  } catch (e) {
    // don't block reducer on transform errors
  }
};

// ─── Selected Premium List Items ─────────────────────────────────────────────

/**
 * Optional benefit param keys that the "Click To Validate" SubmitButton sends
 * but the Coverage Plan step's onLoadApiCalls auto-refresh does not.
 *
 * Used to distinguish a user-driven validate (which should refresh the
 * displayed premium) from an auto-refresh on step re-entry (which would
 * otherwise clobber the previously-validated premium with the base price).
 */
const BENEFIT_PARAM_KEYS = new Set([
  'PersonaAccidentBenefit',
  'motorAssist',
  'windscreen',
  'MechanicalBenefit',
  'MechanicalBenefitValue',
  'ElectricVehicleBatteryReplacementorRepair',
  'electricVehicleBatteryOption',
  'AgreedDepreciation',
]);

const apiCallIncludesBenefitParams = (action) => {
  const params = action?.apiCall?.params;
  if (!Array.isArray(params)) return false;
  return params.some((p) => p?.key && BENEFIT_PARAM_KEYS.has(p.key));
};

/**
 * When a coverage plan is already selected (coverType), build the
 * selectedPremiumListItems array for DisplayListItems and store
 * selectedPremiumRaw. Mutates wizardData in place.
 *
 * @param {object} wizardData — allInitialWizardData to mutate
 * @param {Array}  uiArr — premiumResponseForUI array
 * @param {Array}  motorTT — raw motorTT groups from premium response
 */
const applySelectedCoverType = (wizardData, uiArr, motorTT) => {
  const coverType = wizardData?.coverType;
  if (!coverType || !Array.isArray(uiArr) || uiArr.length === 0) return;

  const selectedUI = uiArr.find((i) => i?.value === coverType) || null;
  if (!selectedUI) return;

  const breakdownItems = selectedUI?.sampleData?.breakdown?.items || [];
  const summaryItem = {
    title: 'Total Premium',
    secondaryTitle: selectedUI?.secondaryTitle,
    secondaryUnits: selectedUI?.secondaryUnits || null,
    secondaryTitleIsCurrency: !!selectedUI?.secondaryTitleIsCurrency,
    secondaryTitleIsNumeric: !!selectedUI?.secondaryTitleIsNumeric,
  };
  wizardData.selectedPremiumListItems = [...breakdownItems, summaryItem];
  wizardData.thirdPartyLiability = 'Included';

  const foundRaw = findRawMotorTTPlan(motorTT, coverType);
  if (foundRaw) {
    wizardData.selectedPremiumRaw = foundRaw;
  }
};

/**
 * Build selectedPremiumListItems when a plan is selected in the
 * ADD_INITIAL_WIZARD_DATA_SUCCESS case. Uses premiumResponseForUI
 * from state and/or finalAllInitialWizardData.
 *
 * Mutates finalAllInitialWizardData in place.
 *
 * @param {object} finalAllInitialWizardData — current wizard data being built
 * @param {object} stateAllInitialWizardData — previous state's allInitialWizardData
 */
export const buildSelectedPremiumFromCoverType = (
  finalAllInitialWizardData,
  stateAllInitialWizardData,
) => {
  try {
    const coverType =
      finalAllInitialWizardData?.coverType || stateAllInitialWizardData?.coverType;
    const uiArr =
      stateAllInitialWizardData?.premiumResponseForUI ||
      finalAllInitialWizardData?.premiumResponseForUI ||
      [];

    if (!coverType || !Array.isArray(uiArr) || uiArr.length === 0) return;

    const selectedUI = uiArr.find((i) => i?.value === coverType) || null;
    if (!selectedUI) return;

    const breakdownItems = selectedUI?.sampleData?.breakdown?.items || [];
    const summaryItem = {
      title: 'Total Premium',
      secondaryTitle: selectedUI?.secondaryTitle,
      secondaryUnits: selectedUI?.secondaryUnits || null,
      secondaryTitleIsCurrency: !!selectedUI?.secondaryTitleIsCurrency,
      secondaryTitleIsNumeric: !!selectedUI?.secondaryTitleIsNumeric,
    };

    finalAllInitialWizardData.selectedPremiumListItems = [...breakdownItems, summaryItem];
    finalAllInitialWizardData.thirdPartyLiability = 'Included';

    // Also store the raw selected plan
    const rawResp =
      stateAllInitialWizardData?.premiumResponse ||
      finalAllInitialWizardData?.premiumResponse;
    if (rawResp) {
      const norm = rawResp?.payload || rawResp;
      const candidate = Array.isArray(norm) ? norm[0] : norm;
      const motorGroups = candidate?.motorTT || [];
      const foundRaw = findRawMotorTTPlan(motorGroups, coverType);
      if (foundRaw) {
        // The cart/receipt `amount` reads selectedPremiumRaw.priceXcg. On a
        // Coverage Plan step re-entry (go Back, change nothing, Continue) the
        // auto-refresh stores a base, no-benefit premiumResponse, so
        // foundRaw.priceXcg here is the BASE price — while the preserve guard in
        // transformPremiumResponse kept the displayed plan/breakdown at the
        // validated with-benefit total. Taking foundRaw verbatim therefore
        // silently under-charges the receipt (e.g. 16,173.17 instead of
        // 20,975.82) even though Step 3 still shows the higher figure.
        //
        // selectedUI.secondaryTitle is the displayed plan price — the same
        // priceXcg the plan was built from at validate time and preserved across
        // the base auto-refresh. Keep priceXcg aligned to it so the receipt
        // always matches the shown Total Premium. When they already agree (the
        // normal validate-then-continue flow) this is a no-op.
        const displayedPrice = Number(selectedUI?.secondaryTitle);
        const hasDisplayedPrice =
          selectedUI?.secondaryTitle != null && Number.isFinite(displayedPrice);
        finalAllInitialWizardData.selectedPremiumRaw =
          hasDisplayedPrice && Number(foundRaw?.priceXcg) !== displayedPrice
            ? { ...foundRaw, priceXcg: displayedPrice }
            : foundRaw;
      }
    }
  } catch (e) {
    // don't block the reducer on this auxiliary transform
  }
};
